import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, errMsg, cleanPhone } from "@/lib/auth";
import { toast } from "sonner";
import { PageHeader } from "@/pages/Classes";
import { Calendar, MapPin, Lock, Sparkles, ExternalLink } from "lucide-react";
import EmailOptInCheckbox, { RequiredMark } from "@/components/EmailOptInCheckbox";
import PhotoConsentCheckbox from "@/components/PhotoConsentCheckbox";
import { PromoCodeInput } from "@/components/PromoCodeInput";

export default function EventDetail() {
  const { eventId } = useParams();
  const [ev, setEv] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", notes: "", email_opt_in: false, photo_consent: false });
  const [promo, setPromo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/events/${eventId}`)
      .then(r => setEv(r.data))
      .catch(() => setNotFound(true));
  }, [eventId]);

  const [sp] = useSearchParams();
  const [resume, setResume] = useState(null);
  useEffect(() => {
    const rid = sp.get("resume"), t = sp.get("t");
    if (!rid || !t) return;
    api.get(`/checkout/resume/event/${rid}`, { params: { t } }).then(r => {
      if (r.data.already_paid) { toast.success("Good news — this ticket is already paid."); return; }
      setForm(f => ({ ...f, ...r.data.form }));
      setResume(r.data);
    }).catch(() => toast.error("That link has expired — please fill in your details below."));
  }, [sp]);
  useEffect(() => {
    if (resume && ev && !submitting) { toast("Resuming your booking…"); submit(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resume, ev]);

  const price = ev ? Number(ev.price || 0) : 0;
  const isPaid = price > 0;
  const soldOut = !!ev?.sold_out; // exact counts are admin-only

  const submit = async () => {
    setSubmitting(true);
    try {
      if (isPaid) {
        const { data } = await api.post("/payments/event-checkout", {
          event_id: eventId, ...form, promo_code: promo?.code || null, origin_url: window.location.origin,
        });
        window.location.href = data.checkout_url;
      } else {
        await api.post(`/events/${eventId}/register`, { event_id: eventId, ...form, origin_url: window.location.origin });
        toast.success("You're in! Check your email for confirmation.");
        setDone(true);
      }
    } catch (e) {
      toast.error(errMsg(e, "Failed to submit"));
      setSubmitting(false);
    }
  };

  if (notFound) return <div className="pt-40 text-center text-zinc-500">Event not found.</div>;
  if (!ev) return <div className="pt-40 text-center text-zinc-500">Loading…</div>;

  return (
    <>
      <PageHeader eyebrow="Event" title={ev.caption || "Event"} back />
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-12 grid md:grid-cols-2 gap-10">
        <div>
          {ev.image && <img src={ev.image} alt={ev.caption} className="w-full aspect-video object-cover mb-6" data-testid="event-hero-image"/>}
          <div className="space-y-3 mb-6 text-sm text-zinc-300">
            {ev.date && <div className="flex items-center gap-2" data-testid="event-date"><Calendar size={16} className="text-red-500"/>{ev.date}</div>}
            {ev.location && <div className="flex items-center gap-2" data-testid="event-location"><MapPin size={16} className="text-red-500"/>{ev.location}</div>}
          </div>
          {ev.description && (
            <div className="prose prose-invert prose-sm max-w-none" data-testid="event-description">
              <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">{ev.description}</p>
            </div>
          )}
        </div>

        <div className="card-surface p-8 h-fit sticky top-24" data-testid="event-register-card">
          {ev.hosting === "external" ? (
            <div data-testid="event-external">
              <div className="eyebrow mb-2">Hosted by {ev.host_name || "our partners"}</div>
              <div className="font-display text-3xl uppercase mb-1">{isPaid ? `$${price} ${ev.currency || "AUD"}` : "Details on host site"}</div>
              <p className="text-zinc-500 text-xs mb-6">Registration and tickets are handled on the organiser's page — we'll take you there.</p>
              <a href={ev.external_url || "#"} target="_blank" rel="noopener noreferrer" className="btn-red w-full flex items-center justify-center gap-2" data-testid="event-external-link">
                {ev.external_label || ev.link_label || "Go to event page"} <ExternalLink size={14}/>
              </a>
            </div>
          ) : (<>
          <div className="eyebrow mb-2">{isPaid ? "Get your ticket" : "RSVP"}</div>
          <div className="font-display text-3xl uppercase mb-1">{isPaid ? `$${promo ? Number(promo.final).toFixed(2) : price} ${ev.currency || "AUD"}` : "Free"}</div>
          {promo && <div className="text-xs text-emerald-400 mb-2" data-testid="event-promo-saving">Promo {promo.code} · −${Number(promo.discount).toFixed(2)}</div>}
          {soldOut && <div className="text-xs uppercase tracking-widest mb-3 text-red-500" data-testid="event-sold-out">Sold out</div>}
          <p className="text-zinc-500 text-xs mb-6">{isPaid ? "Secure Stripe checkout" : "No card needed — just pop your details."}</p>
          {soldOut ? (
            <div className="border border-red-600/40 bg-red-600/10 p-6 text-red-300 text-sm text-center" data-testid="event-sold-out">
              This event is completely sold out. Follow us on Instagram to hear about the next one first.
            </div>
          ) : done ? (
            <div className="border border-emerald-500/40 bg-emerald-500/10 p-6 text-emerald-300 text-sm" data-testid="event-rsvp-done">
              <Sparkles size={18} className="mb-2"/>You're on the list. See you there.
            </div>
          ) : (
            <>
              {["full_name","email","phone"].map(k => (
                <div key={k} className="mb-3">
                  <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                    {k === "full_name" ? "Full name" : k.charAt(0).toUpperCase() + k.slice(1)}
                    <RequiredMark/>
                  </label>
                  <input
                    type={k === "email" ? "email" : k === "phone" ? "tel" : "text"}
                    required
                    aria-required
                    placeholder={k.replace("_"," ")}
                    value={form[k]}
                    onChange={e => setForm({...form,[k]: k === "phone" ? cleanPhone(e.target.value) : e.target.value})}
                    className="w-full bg-black border border-white/10 px-4 py-3 focus:border-red-600 outline-none"
                    data-testid={`event-${k}`}
                  />
                </div>
              ))}
              <textarea placeholder="Notes (optional)" value={form.notes} onChange={e => setForm({...form,notes:e.target.value})} className="w-full bg-black border border-white/10 px-4 py-3 mb-4 focus:border-red-600 outline-none" rows={3} />
              <EmailOptInCheckbox checked={form.email_opt_in} onChange={v => setForm({...form, email_opt_in: v})} testId="event-email-opt-in"/>
              <PhotoConsentCheckbox checked={form.photo_consent} onChange={v => setForm({...form, photo_consent: v})} testId="event-photo-consent"/>
              {isPaid && <PromoCodeInput type="event" amount={price} onApplied={setPromo} testId="event-promo"/>}
              <button onClick={submit} disabled={submitting || !form.full_name || !form.email || !form.phone} className="btn-red w-full disabled:opacity-50 flex items-center justify-center gap-2" data-testid="event-submit">
                {isPaid ? (<><Lock size={14}/>{submitting ? "Redirecting…" : `Pay $${promo ? Number(promo.final).toFixed(2) : price} securely`}</>) : (submitting ? "Sending…" : "Reserve my spot")}
              </button>
            </>
          )}
          </>)}
        </div>
      </div>
    </>
  );
}
